import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import axios from 'axios'
import { removeLoadingKey } from '../../helpers/common'
import { toast } from 'react-toastify';

export const VendorsLoadingKeys = {
    getVendors: "GET_VENDORS",
    upsertVendor: "UPSERT_VENDOR",
    deleteVendor: "DELETE_VENDOR",
    bulkUpdateTerms: "BULK_UPDATE_VENDOR_TERMS",
    bulkAddNote: "BULK_ADD_VENDOR_NOTE",
    bulkAddTeamEmail: "BULK_ADD_TEAM_EMAIL",
    bulkDeleteVendors: "BULK_DELETE_VENDORS",
    deleteProductByVendor: "DELETE_PRODUCT_BY_VENDOR",
    addProductToTheVendor: "ADD_PRODUCT_TO_THE_VENDOR",
    getVendorsBySearch: "GET_VENDORS_BY_SEARCH",
    vendorForView: "VENDOR_VIEW",
    vendorProducts: "GET_VENDOR_PRODUCTS",
    vendorNotes: "GET_VENDOR_NOTES",
    addVendorNote: "ADD_VENDOR_NOTE",
    vendorAuditLog: "GET_VENDOR_AUDIT_LOG",
}

const initialState = {
  vendors: [],
  isFirstLoadComplete: false,
  firstLoadCompleteForProducts: false,
  loadingKeys: [],
  searchProductsByVendor: [],
  vendorProductsData: {},
  vendorForView: {},
  vendorNotes: [],
  vendorAuditLog: [],
  pagination: {
    isTheLastPage: false,
    rangeRows: "",
    totalPages: 0
  },
  loading: false,
}

export const getVendorsRequest = createAsyncThunk(
  'vendors/getVendorsRequest',
  async ({
    search,
    page, 
    itemsPerPage,
    sortBy,
    sortDirection
  }, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`/api/v1/management/vendors`, { params: { 
        search,
        page, 
        itemsPerPage,
        sortBy,
        sortDirection 
      }});
      return data;
    } catch (error) {
      if ('string' === typeof error.response.data) {
        return rejectWithValue({ error: error.response.data })
      } else {
        return rejectWithValue({ error: error.response.data.errors })
      }
    }
  }
)

export const upsertVendorsRequest = createAsyncThunk(
  'vendors/upsertVendorsRequest',
  async ({
    vendorData,
    updateSingleVendor
  }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.post(`/api/v1/management/upsert/vendor`, { 
        vendorData,
        updateSingleVendor
      });
      if (updateSingleVendor && vendorData.id) {
        dispatch(getVendorByIdRequest({ id: vendorData.id }))
      }
      return data;
    } catch (error) {
      if ('string' === typeof error.response.data) {
        return rejectWithValue({ error: error.response.data })
      } else {
        return rejectWithValue({ error: error.response.data.errors })
      }
    }
  }
)

export const getProductsForVendorBySearch = createAsyncThunk(
  'vendors/getProductsForVendorBySearch',
  async ({
    search,
    vendorId
  }, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`/api/v1/management/vendor-products/search`, { params: { 
        search,
        vendorId
      }});
      return data;
    } catch (error) {
      if ('string' === typeof error.response.data) {
        return rejectWithValue({ error: error.response.data })
      } else {
        return rejectWithValue({ error: error.response.data.errors })
      }
    }
  }
)

export const addProductByVendor = createAsyncThunk(
  'vendors/addProductByVendor',
  async ({ 
    vendorId, 
    productId, 
    search, 
    searchProductToAdd,
    page,
    itemsPerPage
  }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.post(`/api/v1/management/vendor-products`, {
        vendorId,
        productId
      });
      dispatch(getVendorProducts({ 
        id: vendorId, 
        search,
        page,
        itemsPerPage
      }));
      dispatch(getProductsForVendorBySearch({
        search: searchProductToAdd,
        vendorId
      }))
      return data;
    } catch (error) {
      if ('string' === typeof error?.response?.data) {
        return rejectWithValue({ error: error?.response?.data })
      } else {
        return rejectWithValue({ error: error?.response?.data?.errors || error?.response?.data?.error })
      }
    }
  }
)

export const deleteProductByVendor = createAsyncThunk(
  'vendors/deleteProductByVendor',
  async ({ 
    vendorId, 
    productId, 
    search,
    page,
    itemsPerPage
  }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.put(`/api/v1/management/soft-delete/vendor-products`, {}, {
        params: {
          vendorId,
          productId
        }
      });
      dispatch(getVendorProducts({ 
        id: vendorId, 
        search,
        page,
        itemsPerPage
      }));
      return data;
    } catch (error) {
      if ('string' === typeof error?.response?.data) {
        return rejectWithValue({ error: error?.response?.data })
      } else {
        return rejectWithValue({ error: error?.response?.data?.errors || error?.response?.data?.error })
      }
    }
  }
)

export const getVendorProducts = createAsyncThunk(
  'vendors/getVendorProducts',
  async ({
    id,
    page,
    itemsPerPage,
    search
  }, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`/api/v1/management/vendor-products`, { params: { 
        id,
        search,
        page,
        itemsPerPage,
      }});
      return data;
    } catch (error) {
      if ('string' === typeof error.response.data) {
        return rejectWithValue({ error: error.response.data })
      } else {
        return rejectWithValue({ error: error.response.data.errors })
      }
    }
  }
)

export const getVendorByIdRequest = createAsyncThunk(
  'vendors/getVendorByIdRequest',
  async ({
    id
  }, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`/api/v1/management/vendor`, { params: { 
        id
      }});
      return data;
    } catch (error) {
      if ('string' === typeof error.response.data) {
        return rejectWithValue({ error: error.response.data })
      } else {
        return rejectWithValue({ error: error.response.data.errors })
      }
    }
  }
)

export const deleteVendor = createAsyncThunk(
  'vendors/deleteVendor',
  async ({ id, search }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.put(`/api/v1/management/soft-delete/vendors/${id}`);
      dispatch(getVendorsRequest({ search }));
      return data;
    } catch (error) {
      if ('string' === typeof error?.response?.data) {
        return rejectWithValue({ error: error?.response?.data, id })
      } else {
        return rejectWithValue({ error: error?.response?.data?.errors || error?.response?.data?.error, id })
      }
    }
  }
)

export const bulkUpdateVendorTerms = createAsyncThunk(
  'vendors/bulkUpdateVendorTerms',
  async ({ vendorIds, terms, refreshParams }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.post('/api/v1/management/vendors/bulk/terms', { vendorIds, terms });
      if (refreshParams) {
        dispatch(getVendorsRequest(refreshParams));
      }
      return data;
    } catch (error) {
      return rejectWithValue({ error: error?.response?.data?.error || error?.response?.data });
    }
  }
)

export const bulkAddVendorNote = createAsyncThunk(
  'vendors/bulkAddVendorNote',
  async ({ vendorIds, note, refreshParams }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.post('/api/v1/management/vendors/bulk/note', { vendorIds, note });
      if (refreshParams) {
        dispatch(getVendorsRequest(refreshParams));
      }
      return data;
    } catch (error) {
      return rejectWithValue({ error: error?.response?.data?.error || error?.response?.data });
    }
  }
)

export const bulkAddTeamEmail = createAsyncThunk(
  'vendors/bulkAddTeamEmail',
  async ({ vendorIds, email, refreshParams }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.post('/api/v1/management/vendors/bulk/team-email', { vendorIds, email });
      if (refreshParams) {
        dispatch(getVendorsRequest(refreshParams));
      }
      return data;
    } catch (error) {
      return rejectWithValue({ error: error?.response?.data?.error || error?.response?.data });
    }
  }
)

export const bulkDeleteVendors = createAsyncThunk(
  'vendors/bulkDeleteVendors',
  async ({ vendorIds, refreshParams }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.put('/api/v1/management/vendors/bulk/delete', { vendorIds });
      if (refreshParams) {
        dispatch(getVendorsRequest(refreshParams));
      }
      return data;
    } catch (error) {
      return rejectWithValue({ error: error?.response?.data?.error || error?.response?.data });
    }
  }
)

export const getVendorNotesRequest = createAsyncThunk(
  'vendors/getVendorNotesRequest',
  async ({ vendorId }, { rejectWithValue }) => {
    try {
      const { data } = await axios.get('/api/v1/management/vendor-notes', { params: { vendorId } });
      return data;
    } catch (error) {
      return rejectWithValue({ error: error?.response?.data?.error || error?.response?.data });
    }
  }
)

export const getVendorAuditLogRequest = createAsyncThunk(
  'vendors/getVendorAuditLogRequest',
  async ({ vendorId } = {}, { rejectWithValue }) => {
    try {
      const { data } = await axios.get('/api/v1/management/vendor-audit-log', { params: vendorId ? { vendorId } : {} });
      return data;
    } catch (error) {
      return rejectWithValue({ error: error?.response?.data?.error || error?.response?.data });
    }
  }
)

export const addVendorNoteRequest = createAsyncThunk(
  'vendors/addVendorNoteRequest',
  async ({ vendorId, note }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post('/api/v1/management/vendor-notes', { vendorId, note });
      return data;
    } catch (error) {
      return rejectWithValue({ error: error?.response?.data?.error || error?.response?.data });
    }
  }
)

export const vendorsSlice = createSlice({
  name: 'vendors',
  initialState,
  reducers: {
    cleanUpVendorProducts: (state, action) => {
      state.vendorProductsData = {};
    },
    cleanUpSearchProductsForVendor: (state, action) => {
      state.searchProductsByVendor = [];
    },
    cleanUpVendorForView: (state, action) => {
      state.vendorForView = {};
      state.vendorNotes = [];
      state.vendorAuditLog = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getVendorsRequest.pending, (state) => {
        state.loading = true;
        state.loadingKeys = [...state.loadingKeys, VendorsLoadingKeys.getVendors];
      })
      .addCase(getVendorsRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.vendors = action.payload?.items;
        state.isFirstLoadComplete = true;
        state.pagination.isTheLastPage = action.payload?.isTheLastPage;
        state.pagination.rangeRows = action.payload?.rangeRows;
        state.pagination.totalPages = action.payload?.totalPages;
        state.loadingKeys = removeLoadingKey(VendorsLoadingKeys.getVendors, state.loadingKeys);
      })
      .addCase(getVendorsRequest.rejected, (state) => {
        state.loading = false;
        state.isFirstLoadComplete = true;
        state.pagination.isTheLastPage = false;
        state.pagination.rangeRows = "";
        state.pagination.totalPages = 0;
        state.loadingKeys = removeLoadingKey(VendorsLoadingKeys.getVendors, state.loadingKeys);
      })
      .addCase(upsertVendorsRequest.pending, (state) => {
        state.loading = true;
        state.loadingKeys = [...state.loadingKeys, VendorsLoadingKeys.upsertVendor];
      })
      .addCase(upsertVendorsRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.loadingKeys = removeLoadingKey(VendorsLoadingKeys.upsertVendor, state.loadingKeys);
        if (!action?.meta?.arg?.updateSingleVendor) {
          toast.success(`Vendor created successfully.`, { position: "bottom-right" })
        }
        if (action?.meta?.arg?.updateSingleVendor) {
          toast.warn("Vendor updated successfully. But the vendor is not synced to ShipHero.", { position: "bottom-right" })
        } 
      })
      .addCase(upsertVendorsRequest.rejected, (state, action) => {
        state.loading = false;
        state.loadingKeys = removeLoadingKey(VendorsLoadingKeys.upsertVendor, state.loadingKeys);
        toast.error(
          `Vendor can't be updated. Error - ${action.error?.response?.data?.error || action.error?.message}.`, { position: "bottom-right" }
        )
      })
      .addCase(addProductByVendor.pending, (state) => {
        state.loading = true;
        state.loadingKeys = [...state.loadingKeys, VendorsLoadingKeys.addProductToTheVendor];
      })
      .addCase(addProductByVendor.fulfilled, (state, action) => {
        state.loading = false;
        state.loadingKeys = removeLoadingKey(VendorsLoadingKeys.addProductToTheVendor, state.loadingKeys);
        toast.success(`Product was added successfully.`, { position: "bottom-right" })
      })
      .addCase(addProductByVendor.rejected, (state, action) => {
        state.loading = false;
        state.loadingKeys = removeLoadingKey(VendorsLoadingKeys.addProductToTheVendor, state.loadingKeys);
        toast.error(
          `Product can't be added. Error - ${action.payload?.error}.`, { position: "bottom-right" }
        )
      })
      .addCase(deleteProductByVendor.pending, (state) => {
        state.loading = true;
        state.loadingKeys = [...state.loadingKeys, VendorsLoadingKeys.deleteProductByVendor];
      })
      .addCase(deleteProductByVendor.fulfilled, (state) => {
          state.loading = false;
          state.loadingKeys = removeLoadingKey(VendorsLoadingKeys.deleteProductByVendor, state.loadingKeys);
          toast.success(`Product deleted successfully.`, { position: "bottom-right" })
      })
      .addCase(deleteProductByVendor.rejected, (state, action) => {
          state.loading = false;
          state.loadingKeys = removeLoadingKey(VendorsLoadingKeys.deleteProductByVendor, state.loadingKeys);
          toast.error(
            `Product can't be deleted. Error - ${action.error?.message}.`, { position: "bottom-right" }
          )
      })
      .addCase(getProductsForVendorBySearch.pending, (state) => {
        state.loading = true;
        state.loadingKeys = [...state.loadingKeys, VendorsLoadingKeys.getVendorsBySearch];
      })
      .addCase(getProductsForVendorBySearch.fulfilled, (state, action) => {
        state.loading = false;
        state.searchProductsByVendor = action.payload;
        state.loadingKeys = removeLoadingKey(VendorsLoadingKeys.getVendorsBySearch, state.loadingKeys);
      })
      .addCase(getProductsForVendorBySearch.rejected, (state) => {
        state.loading = false;
        state.loadingKeys = removeLoadingKey(VendorsLoadingKeys.getVendorsBySearch, state.loadingKeys);
      })
      .addCase(deleteVendor.pending, (state) => {
        state.loading = true;
        state.loadingKeys = [...state.loadingKeys, VendorsLoadingKeys.deleteVendor];
      })
      .addCase(deleteVendor.fulfilled, (state) => {
          state.loading = false;
          state.loadingKeys = removeLoadingKey(VendorsLoadingKeys.deleteVendor, state.loadingKeys);
          toast.success(`Vendor deleted successfully.`, { position: "bottom-right" })
      })
      .addCase(deleteVendor.rejected, (state, action) => {
          state.loading = false;
          state.loadingKeys = removeLoadingKey(VendorsLoadingKeys.deleteVendor, state.loadingKeys);
          toast.error(
            `Vendor can't be deleted. Error - ${action.error?.message}.`, { position: "bottom-right" }
          )
      })
      .addCase(bulkUpdateVendorTerms.pending, (state) => {
        state.loading = true;
        state.loadingKeys = [...state.loadingKeys, VendorsLoadingKeys.bulkUpdateTerms];
      })
      .addCase(bulkUpdateVendorTerms.fulfilled, (state) => {
        state.loading = false;
        state.loadingKeys = removeLoadingKey(VendorsLoadingKeys.bulkUpdateTerms, state.loadingKeys);
        toast.success('Vendor terms updated.', { position: 'bottom-right' });
      })
      .addCase(bulkUpdateVendorTerms.rejected, (state, action) => {
        state.loading = false;
        state.loadingKeys = removeLoadingKey(VendorsLoadingKeys.bulkUpdateTerms, state.loadingKeys);
        toast.error(`Failed to update terms. ${action.payload?.error || ''}`, { position: 'bottom-right' });
      })
      .addCase(bulkAddVendorNote.pending, (state) => {
        state.loading = true;
        state.loadingKeys = [...state.loadingKeys, VendorsLoadingKeys.bulkAddNote];
      })
      .addCase(bulkAddVendorNote.fulfilled, (state) => {
        state.loading = false;
        state.loadingKeys = removeLoadingKey(VendorsLoadingKeys.bulkAddNote, state.loadingKeys);
        toast.success('Note added to vendors.', { position: 'bottom-right' });
      })
      .addCase(bulkAddVendorNote.rejected, (state, action) => {
        state.loading = false;
        state.loadingKeys = removeLoadingKey(VendorsLoadingKeys.bulkAddNote, state.loadingKeys);
        toast.error(`Failed to add note. ${action.payload?.error || ''}`, { position: 'bottom-right' });
      })
      .addCase(bulkAddTeamEmail.pending, (state) => {
        state.loading = true;
        state.loadingKeys = [...state.loadingKeys, VendorsLoadingKeys.bulkAddTeamEmail];
      })
      .addCase(bulkAddTeamEmail.fulfilled, (state) => {
        state.loading = false;
        state.loadingKeys = removeLoadingKey(VendorsLoadingKeys.bulkAddTeamEmail, state.loadingKeys);
        toast.success('Team email added to vendors.', { position: 'bottom-right' });
      })
      .addCase(bulkAddTeamEmail.rejected, (state, action) => {
        state.loading = false;
        state.loadingKeys = removeLoadingKey(VendorsLoadingKeys.bulkAddTeamEmail, state.loadingKeys);
        toast.error(`Failed to add team email. ${action.payload?.error || ''}`, { position: 'bottom-right' });
      })
      .addCase(bulkDeleteVendors.pending, (state) => {
        state.loading = true;
        state.loadingKeys = [...state.loadingKeys, VendorsLoadingKeys.bulkDeleteVendors];
      })
      .addCase(bulkDeleteVendors.fulfilled, (state) => {
        state.loading = false;
        state.loadingKeys = removeLoadingKey(VendorsLoadingKeys.bulkDeleteVendors, state.loadingKeys);
        toast.success('Vendors deleted successfully.', { position: 'bottom-right' });
      })
      .addCase(bulkDeleteVendors.rejected, (state, action) => {
        state.loading = false;
        state.loadingKeys = removeLoadingKey(VendorsLoadingKeys.bulkDeleteVendors, state.loadingKeys);
        toast.error(`Failed to delete vendors. ${action.payload?.error || ''}`, { position: 'bottom-right' });
      })
      .addCase(getVendorProducts.pending, (state) => {
        state.loading = true;
        state.loadingKeys = [...state.loadingKeys, VendorsLoadingKeys.vendorProducts];
      })
      .addCase(getVendorProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.firstLoadCompleteForProducts = true;
        state.vendorProductsData = action.payload?.data;
        state.loadingKeys = removeLoadingKey(VendorsLoadingKeys.vendorProducts, state.loadingKeys);
      })
      .addCase(getVendorProducts.rejected, (state) => {
        state.loading = false;
        state.firstLoadCompleteForProducts = true;
        state.loadingKeys = removeLoadingKey(VendorsLoadingKeys.vendorProducts, state.loadingKeys);
      })
      .addCase(getVendorByIdRequest.pending, (state) => {
        state.loading = true;
        state.loadingKeys = [...state.loadingKeys, VendorsLoadingKeys.vendorForView];
      })
      .addCase(getVendorByIdRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.isFirstLoadComplete = true;
        state.vendorForView = action.payload?.vendor;
        state.loadingKeys = removeLoadingKey(VendorsLoadingKeys.vendorForView, state.loadingKeys);
      })
      .addCase(getVendorByIdRequest.rejected, (state) => {
        state.loading = false;
        state.isFirstLoadComplete = true;
        state.loadingKeys = removeLoadingKey(VendorsLoadingKeys.vendorForView, state.loadingKeys);
      })
      .addCase(getVendorNotesRequest.pending, (state) => {
        state.loadingKeys = [...state.loadingKeys, VendorsLoadingKeys.vendorNotes];
      })
      .addCase(getVendorNotesRequest.fulfilled, (state, action) => {
        state.vendorNotes = action.payload?.items || [];
        state.loadingKeys = removeLoadingKey(VendorsLoadingKeys.vendorNotes, state.loadingKeys);
      })
      .addCase(getVendorNotesRequest.rejected, (state) => {
        state.loadingKeys = removeLoadingKey(VendorsLoadingKeys.vendorNotes, state.loadingKeys);
      })
      .addCase(getVendorAuditLogRequest.pending, (state) => {
        state.loadingKeys = [...state.loadingKeys, VendorsLoadingKeys.vendorAuditLog];
      })
      .addCase(getVendorAuditLogRequest.fulfilled, (state, action) => {
        state.vendorAuditLog = action.payload?.items || [];
        state.loadingKeys = removeLoadingKey(VendorsLoadingKeys.vendorAuditLog, state.loadingKeys);
      })
      .addCase(getVendorAuditLogRequest.rejected, (state) => {
        state.loadingKeys = removeLoadingKey(VendorsLoadingKeys.vendorAuditLog, state.loadingKeys);
      })
      .addCase(addVendorNoteRequest.pending, (state) => {
        state.loadingKeys = [...state.loadingKeys, VendorsLoadingKeys.addVendorNote];
      })
      .addCase(addVendorNoteRequest.fulfilled, (state, action) => {
        if (action.payload?.item) {
          state.vendorNotes = [action.payload.item, ...state.vendorNotes];
        }
        state.loadingKeys = removeLoadingKey(VendorsLoadingKeys.addVendorNote, state.loadingKeys);
      })
      .addCase(addVendorNoteRequest.rejected, (state, action) => {
        state.loadingKeys = removeLoadingKey(VendorsLoadingKeys.addVendorNote, state.loadingKeys);
        toast.error(`Failed to add note. ${action.payload?.error || ''}`, { position: 'bottom-right' });
      })
  }
})

export const { cleanUpVendorProducts, cleanUpSearchProductsForVendor, cleanUpVendorForView } = vendorsSlice.actions;

export default vendorsSlice.reducer
